import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Save, Calendar, Users, Clock, Repeat, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { gymModalInput, gymModalLabel } from '../gymPageTheme';

const COLORS = [
    { name: 'Blu', value: '#3B82F6' },
    { name: 'Verde', value: '#10B981' },
    { name: 'Rosso', value: '#EF4444' },
    { name: 'Giallo', value: '#F59E0B' },
    { name: 'Viola', value: '#8B5CF6' },
    { name: 'Rosa', value: '#EC4899' },
    { name: 'Indaco', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' }
];

const CATEGORIES = [
    'Yoga',
    'Pilates',
    'CrossFit',
    'Spinning',
    'Zumba',
    'Aerobica',
    'Functional',
    'Boxe',
    'Kick Boxing',
    'Calisthenics',
    'Altro'
];

const DAYS_OF_WEEK = [
    { key: 'monday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.monday' },
    { key: 'tuesday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.tuesday' },
    { key: 'wednesday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.wednesday' },
    { key: 'thursday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.thursday' },
    { key: 'friday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.friday' },
    { key: 'saturday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.saturday' },
    { key: 'sunday', labelKey: 'gym.courses.addRecurrenceModal.daysOfWeek.sunday' }
];

export default function CreateCourseModal({ isOpen, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        max_participants: '',
        is_unlimited: false,
        is_recurring: true, // Sempre true
        recurrence_type: 'weekly',
        recurrence_days: [],
        recurrence_end_date: '',
        standard_start_time: '09:00',
        standard_end_time: '10:00',
        color: '#3B82F6'
    });
    
    // Stati per logo upload
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Reset error quando l'utente modifica
        if (error) setError(null);
    };

    const handleDayToggle = (dayKey) => {
        setFormData(prev => ({
            ...prev,
            recurrence_days: prev.recurrence_days.includes(dayKey)
                ? prev.recurrence_days.filter(d => d !== dayKey)
                : [...prev.recurrence_days, dayKey]
        }));
    };
    
    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validazione tipo file
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Tipo file non supportato. Usa PNG, JPG o WEBP');
            return;
        }
        
        // Validazione dimensione (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File troppo grande. Massimo 2MB');
            return;
        }
        
        setLogoFile(file);
        
        // Crea preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };
    
    const handleUploadLogo = async (courseId) => {
        if (!logoFile || !courseId) return;
        
        try {
            setUploadingLogo(true);
            const api = getAuthenticatedApi();
            
            const formData = new FormData();
            formData.append('logo', logoFile);
            
            const response = await api.post(
                `/course_logo_upload.php?course_id=${courseId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.success) {
                const timestamp = response.data.timestamp || Date.now();
                setLogoPreview(`/api/serve_image.php?filename=${response.data.filename}&type=logo&v=${timestamp}`);
                setLogoFile(null);
                toast.success('Logo caricato con successo!');
            }
        } catch (err) {
            console.error('Errore upload logo corso:', err);
            toast.error(err.response?.data?.error || 'Errore nel caricamento del logo');
        } finally {
            setUploadingLogo(false);
        }
    };
    
    const handleDeleteLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        toast.success('Preview logo rimossa');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validazione
        if (!formData.title.trim()) {
            setError(t('gym.courses.createModal.titleRequired'));
            return;
        }
        
        // Validazione lezioni programmate (sempre obbligatorie)
        if (formData.recurrence_days.length === 0) {
            setError(t('gym.courses.createModal.selectDaysRequired'));
            return;
        }
        if (!formData.recurrence_end_date) {
            setError(t('gym.courses.createModal.endDateRequired'));
            return;
        }
        if (!formData.standard_start_time || !formData.standard_end_time) {
            setError(t('gym.courses.createModal.timeRequired'));
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            
            // Prepara dati per l'API
            const courseData = {
                title: formData.title.trim(),
                description: formData.description.trim() || null,
                category: formData.category || null,
                is_unlimited: formData.is_unlimited,
                max_participants: formData.is_unlimited ? null : (parseInt(formData.max_participants) || null),
                is_recurring: formData.is_recurring,
                color: formData.color
            };
            
            // Aggiungi sempre i dati delle lezioni programmate (corso ricorrente)
            courseData.recurrence_type = formData.recurrence_type;
            courseData.recurrence_days = formData.recurrence_days;
            courseData.recurrence_end_date = formData.recurrence_end_date;
            courseData.standard_start_time = formData.standard_start_time + ':00';
            courseData.standard_end_time = formData.standard_end_time + ':00';
            
            const response = await api.post('/gym_courses.php?action=create_course', courseData);
            
            if (response.data.success) {
                const courseId = response.data.course_id;
                
                // Carica logo se presente
                if (logoFile && courseId) {
                    await handleUploadLogo(courseId);
                }
                
                // Mostra messaggio di successo
                const sessionsCount = response.data.sessions_generated?.sessions_created || 0;
                if (sessionsCount > 0) {
                    toast.success(t('gym.courses.createModal.success', { count: sessionsCount }));
                }
                onSuccess();
            } else {
                throw new Error(response.data.error || t('gym.courses.errors.createError'));
            }
            
        } catch (err) {
            console.error('Errore creazione corso:', err);
            setError(err.response?.data?.error || err.message || t('gym.courses.errors.createError'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="w-full">
                {/* Error */}
                {error && (
                    <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Informazioni Base */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Calendar size={18} />
                            {t('gym.courses.createModal.basicInfo')}
                        </h4>
                        
                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.courses.createModal.titleLabel')} *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className={gymModalInput}
                                placeholder={t('gym.courses.createModal.titlePlaceholder')}
                                required
                            />
                        </div>

                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.courses.createModal.descriptionLabel')}
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={3}
                                className={gymModalInput}
                                placeholder={t('gym.courses.createModal.descriptionPlaceholder')}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.categoryLabel')}
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    className={gymModalInput}
                                >
                                    <option value="">{t('gym.courses.createModal.categoryPlaceholder')}</option>
                                    {CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.colorLabel')}
                                </label>
                                <div className="flex gap-2">
                                    {COLORS.map(color => (
                                        <button
                                            key={color.value}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                                            className={`w-8 h-8 rounded-full border-2 transition-transform ${
                                                formData.color === color.value 
                                                    ? 'border-gray-900 dark:border-white scale-110' 
                                                    : 'border-transparent hover:scale-110'
                                            }`}
                                            style={{ backgroundColor: color.value }}
                                            title={color.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Logo Corso */}
                        <div>
                            <label className={`${gymModalLabel} mb-2`}>
                                Logo Corso (opzionale)
                            </label>
                            
                            <div className="space-y-3">
                                {/* Preview Logo */}
                                {logoPreview && (
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <img 
                                                src={logoPreview} 
                                                alt="Logo corso" 
                                                className="h-20 w-20 rounded-lg border-2 border-gray-300 object-cover dark:border-white/15"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleDeleteLogo}
                                            className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={16} />
                                            <span className="text-sm">Rimuovi</span>
                                        </button>
                                    </div>
                                )}
                                
                                {/* Upload Input */}
                                <div className="flex items-center gap-3">
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        onChange={handleLogoFileChange}
                                        className="hidden"
                                        id="create-course-logo-input"
                                    />
                                    <label
                                        htmlFor="create-course-logo-input"
                                        className="flex cursor-pointer items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-white/90 dark:hover:brightness-110"
                                    >
                                        <ImageIcon size={18} />
                                        {logoPreview ? 'Cambia Logo' : 'Scegli Logo'}
                                    </label>
                                </div>
                                
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG o WEBP. Max 2MB, 1500x1500px. Consigliato: 200-400px
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Capacità Partecipanti */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Users size={18} />
                            {t('gym.courses.createModal.capacityTitle')}
                        </h4>
                        
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="is_unlimited"
                                name="is_unlimited"
                                checked={formData.is_unlimited}
                                onChange={handleChange}
                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            />
                            <label htmlFor="is_unlimited" className="text-sm text-gray-700 dark:text-white/85">
                                {t('gym.courses.createModal.unlimitedParticipants')}
                            </label>
                        </div>

                        {!formData.is_unlimited && (
                            <div>
                                <label className={gymModalLabel}>
                                    {t('gym.courses.createModal.maxParticipantsLabel')}
                                </label>
                                <input
                                    type="number"
                                    name="max_participants"
                                    value={formData.max_participants}
                                    onChange={handleChange}
                                    min="1"
                                    className={gymModalInput}
                                    placeholder={t('gym.courses.createModal.maxParticipantsPlaceholder')}
                                />
                            </div>
                        )}
                    </div>

                    {/* Lezioni programmate */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Repeat size={18} />
                            {t('gym.courses.createModal.recurrenceTitle')}
                        </h4>
                        
                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                            <p className="text-blue-700 dark:text-blue-400 text-sm">
                                {t('gym.courses.createModal.recurringCourse')}
                            </p>
                        </div>
                            <div className="space-y-4 pl-7 border-l-2 border-indigo-200 dark:border-indigo-800">
                                {/* Giorni della settimana */}
                                <div>
                                    <label className={`${gymModalLabel} mb-2`}>
                                        {t('gym.courses.createModal.selectDays')} *
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {DAYS_OF_WEEK.map(day => (
                                            <button
                                                key={day.key}
                                                type="button"
                                                onClick={() => handleDayToggle(day.key)}
                                                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                                    formData.recurrence_days.includes(day.key)
                                                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
                                                        : 'border-gray-300 text-gray-700 hover:border-indigo-300 dark:border-gray-700 dark:text-white/85 dark:hover:border-indigo-400'
                                                }`}
                                            >
                                                {t(day.labelKey)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Orari */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={gymModalLabel}>
                                            {t('gym.courses.createModal.startTimeLabel')} *
                                        </label>
                                        <input
                                            type="time"
                                            name="standard_start_time"
                                            value={formData.standard_start_time}
                                            onChange={handleChange}
                                            className={gymModalInput}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className={gymModalLabel}>
                                            {t('gym.courses.createModal.endTimeLabel')} *
                                        </label>
                                        <input
                                            type="time"
                                            name="standard_end_time"
                                            value={formData.standard_end_time}
                                            onChange={handleChange}
                                            className={gymModalInput}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Data fine lezioni */}
                                <div>
                                    <label className={gymModalLabel}>
                                        {t('gym.courses.createModal.endDateLabel')} *
                                    </label>
                                    <input
                                        type="date"
                                        name="recurrence_end_date"
                                        value={formData.recurrence_end_date}
                                        onChange={handleChange}
                                        min={new Date().toISOString().split('T')[0]}
                                        className={gymModalInput}
                                        required={formData.is_recurring}
                                    />
                                </div>
                            </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-white/85 dark:hover:bg-gray-700"
                        >
                            {t('gym.courses.createModal.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>{t('gym.courses.createModal.creating')}</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    <span>{t('gym.courses.createModal.create')}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
        </div>
    );
}

